import { gql } from 'graphql-request';

import { gqlClient } from 'graphql/gqlClient';
import { EditableSession } from 'types/event.types';
import { UpdateEventNotificationSettingsInput } from '@event-editor/api/eventBasicDetails';

import { EventChangesFragment, EventBasicDetailsChanges, PlusOneConfigFragment } from './common';

type SortParamsInput = {
  sortBy: 'start_datetime';
  sortOrder: 'ASC' | 'DESC';
};

const ListEventSessionsQuery = gql`
  query listEditorEventSessions($eventId: Int!, $sortParams: SortParamsInput) {
    listEditorEventSessions(eventId: $eventId, sortParams: $sortParams) {
      id
      title
      description
      datetimeScheduled
      datetimeEnd
      allDay
      timezoneCode
      isInPerson
      locationAddress
      isVirtual
      virtualLocation
      targetAttendance
      maxAttendees
      hasEnded
      hasStarted
      isPublished
      isGuestCheckinOpen
      totalCheckedInGuests
      isUsingEstimatedAttendees
      estimatedAttendees
      totalDeactivatedGuests
      zoomMeeting {
        id
      }
      googleMeeting {
        id
      }
      guestListTotals {
        confirmed
        attended
        incomplete
        waitlisted
      }
      plusOneConfig {
        ...PlusOneConfigFragment
      }
    }
  }
  ${PlusOneConfigFragment}
`;

type PlusOneConfigPayload = {
  allowPlusOnes: boolean;
  maxPlusOnes: number;
  collectQuestionnaireResponses: boolean;
};

type BaseSessionPayload = {
  title: string;
  description: string;
  startDatetime: string;
  endDatetime: string;
  allDay: boolean;
  isInPerson: boolean;
  isVirtual: boolean;
  inPersonLocation?: string;
  virtualLink?: string;
  zoomMeetingId?: number;
  googleMeetingId?: number;
  maxAttendees?: number;
  targetAttendance?: number;
  plusOneConfig?: PlusOneConfigPayload;
};

type CreateOrUpdateSessionResult = {
  ok: boolean;
  eventSession: EditableSession;
};

type CreateDraftSessionPayload = Partial<BaseSessionPayload>;

type CreateDraftSessionInput = {
  eventId: number;
  payload: CreateDraftSessionPayload;
};

const CreateDraftSessionMutation = gql`
  mutation createDraftSession($input: CreateDraftEventSessionInput!) {
    createDraftEventSession(input: $input) {
      ok
      eventSession {
        id
      }
    }
  }
`;

type UpdateSessionPayload = BaseSessionPayload & {
  sessionId: number;
};

type UpdateSessionInput = {
  eventId: number;
  payload: UpdateSessionPayload;
  updateNotificationSettings?: UpdateEventNotificationSettingsInput;
};

const UpdateSessionMutation = gql`
  mutation updateEventSession($input: UpdateEventSessionInput!) {
    updateEventSession(input: $input) {
      ok
      eventSession {
        id
      }
    }
  }
`;

type DuplicateSessionInput = {
  sessionId: number;
  changesPayload?: UpdateSessionPayload;
  updateNotificationSettings?: UpdateEventNotificationSettingsInput;
};

const DuplicateSessionMutation = gql`
  mutation duplicateEventSession($input: DuplicateSessionInputType!) {
    duplicateEventSession(input: $input) {
      ok
    }
  }
`;

const GetSessionChangesQuery = gql`
  query eventSessionChanges($eventId: Int!, $payload: UpdateEventSessionPayloadInput!) {
    eventSessionChanges(eventId: $eventId, payload: $payload) {
      ...EventChangesFragment
    }
  }
  ${EventChangesFragment}
`;

interface PublishEventSessionInput extends BaseSessionPayload {
  eventId: number;
  sessionId?: number;
}

type PublishEventSessionResult = {
  ok: boolean;
  eventSession: EditableSession;
};

const PublishSessionMutation = gql`
  mutation publishEventSession($input: PublishEventSessionPayloadInput!) {
    publishEventSession(input: $input) {
      ok
      eventSession {
        id
      }
    }
  }
`;

type DeleteSessionInput = {
  eventId: number;
  sessionId: number;
};

const DeleteSessionMutation = gql`
  mutation deleteEventSession($eventId: Int!, $sessionId: Int!) {
    deleteEventSession(eventId: $eventId, sessionId: $sessionId) {
      ok
    }
  }
`;

const eventSessionsApi = {
  listEditorSessions: async ({
    eventId,
    sortParams
  }: {
    eventId: number;
    sortParams?: SortParamsInput;
  }): Promise<EditableSession[]> => {
    const res = await gqlClient.request<{ listEditorEventSessions: EditableSession[] }>(
      ListEventSessionsQuery,
      {
        eventId,
        sortParams
      }
    );
    return res.listEditorEventSessions;
  },

  createDraft: async ({
    eventId,
    payload
  }: CreateDraftSessionInput): Promise<CreateOrUpdateSessionResult> =>
    gqlClient
      .request<{ createDraftEventSession: CreateOrUpdateSessionResult }>(
        CreateDraftSessionMutation,
        {
          input: {
            eventId,
            payload
          }
        }
      )
      .then((res) => res.createDraftEventSession),

  update: async ({
    eventId,
    payload,
    updateNotificationSettings
  }: UpdateSessionInput): Promise<CreateOrUpdateSessionResult> =>
    gqlClient
      .request<{ updateEventSession: CreateOrUpdateSessionResult }>(UpdateSessionMutation, {
        input: { eventId, payload, updateNotificationSettings }
      })
      .then((res) => res.updateEventSession),

  duplicate: async ({
    sessionId,
    changesPayload,
    updateNotificationSettings
  }: DuplicateSessionInput): Promise<{ ok: boolean }> =>
    gqlClient
      .request<{ duplicateEventSession: { ok: boolean } }>(DuplicateSessionMutation, {
        input: { sessionId, changesPayload, updateNotificationSettings }
      })
      .then((res) => res.duplicateEventSession),

  getChanges: async ({
    eventId,
    payload
  }: {
    eventId: number;
    payload: UpdateSessionPayload;
  }): Promise<EventBasicDetailsChanges> =>
    gqlClient
      .request<{ eventSessionChanges: EventBasicDetailsChanges }>(GetSessionChangesQuery, {
        eventId,
        payload
      })
      .then((res) => res.eventSessionChanges),

  publish: async (input: PublishEventSessionInput): Promise<PublishEventSessionResult> =>
    gqlClient
      .request<{ publishEventSession: PublishEventSessionResult }>(PublishSessionMutation, {
        input
      })
      .then((res) => res.publishEventSession),

  delete: async ({ eventId, sessionId }: DeleteSessionInput): Promise<{ ok: boolean }> =>
    gqlClient
      .request<{ deleteEventSession: { ok: boolean } }>(DeleteSessionMutation, {
        eventId,
        sessionId
      })
      .then((res) => res.deleteEventSession)
};

export {
  eventSessionsApi,
  type CreateDraftSessionPayload,
  type UpdateSessionPayload,
  type PublishEventSessionInput
};
